import React, { useState, useCallback, useMemo } from 'react';
import {
  BarChart, Bar, Scatter, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, Line, ComposedChart,
} from 'recharts';
import dayjs from 'dayjs';
import { useApi } from '../../hooks/useApi';
import Loader from '../common/Loader';
import DateRangeFilter from './DateRangeFilter';
import {
  fetchTasksInRange, processEstimatedTimeData, getPresetDates,
  formatSeconds, RESOLUTION_COLORS, TOOLTIP_STYLE, AXIS_TICK, CURVE_TYPES,
} from './telemetryUtils';

const EstimatedTime = () => {
  const { apiCall } = useApi();
  const [preset, setPreset] = useState('month');
  const [startDate, setStartDate] = useState(() => getPresetDates('month').start);
  const [endDate, setEndDate] = useState(() => dayjs());
  const [typeFilter, setTypeFilter] = useState('all');
  const [curveType, setCurveType] = useState('linear');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const handleApply = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const tasks = await fetchTasksInRange(apiCall, startDate, endDate);
      const processed = processEstimatedTimeData(tasks, typeFilter);
      setData(processed);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [apiCall, startDate, endDate, typeFilter]);

  const handleTypeChange = (newType) => {
    setTypeFilter(newType);
  };

  // Get current curve fits for the selected curve type
  const currentCurves = useMemo(() => {
    if (!data) return null;
    return {
      r1080p: data.curves1080p[curveType],
      r720p: data.curves720p[curveType],
      rAll: data.curvesAll[curveType],
    };
  }, [data, curveType]);

  const renderFormula = () => {
    if (!data || !currentCurves) return null;
    const { r1080p, r720p, rAll } = currentCurves;
    const { avgChars } = data;
    const MIN_SAMPLES = 2;

    const fmt = (r) => {
      if (r.sampleCount < MIN_SAMPLES) {
        return r.sampleCount === 0
          ? 'No data'
          : `Insufficient data (${r.sampleCount} sample, need ${MIN_SAMPLES}+)`;
      }
      return r.formula();
    };
    const estimate = (r) => {
      if (r.sampleCount < MIN_SAMPLES || !avgChars) return null;
      const result = r.predict(avgChars);
      return result > 0 ? Math.round(result * 10) / 10 : 0;
    };
    const label = (r, outliers) => {
      if (r.sampleCount < MIN_SAMPLES) return '';
      const parts = [`${r.sampleCount} samples`, `R²=${Math.round(r.r2 * 1000) / 1000}`];
      if (outliers > 0) parts.push(`${outliers} outliers removed`);
      return `(${parts.join(', ')})`;
    };

    const outliers = data.regressionOutliers || {};
    const renderItem = (name, r, outlierKey) => (
      <div className="formula-item" key={name}>
        <span className="formula-label">{name} <span className="formula-samples">{label(r, outliers[outlierKey] || 0)}</span></span>
        <span className={`formula-value${r.sampleCount < MIN_SAMPLES ? ' formula-insufficient' : ''}`}>
          {fmt(r)}
        </span>
        {estimate(r) != null && (
          <span className="formula-estimate">@ {avgChars} avg chars = {formatSeconds(estimate(r))}</span>
        )}
      </div>
    );

    // Find best R² across curve types for combined data
    const bestCurve = CURVE_TYPES.reduce((best, ct) => {
      const r = data.curvesAll[ct.key];
      return (r.sampleCount >= MIN_SAMPLES && r.r2 > (best?.r2 || -1)) ? { ...ct, r2: r.r2 } : best;
    }, null);

    return (
      <div className="formula-card">
        <div className="formula-header">
          <h3 className="card-title">Derived Estimated Time Formula</h3>
          <div className="curve-selector">
            {CURVE_TYPES.map((ct) => (
              <button
                key={ct.key}
                className={`filter-preset-btn${curveType === ct.key ? ' active' : ''}`}
                onClick={() => setCurveType(ct.key)}
              >
                {ct.label}
              </button>
            ))}
          </div>
        </div>
        {bestCurve && bestCurve.key !== curveType && (
          <div className="formula-best-hint">
            Best fit: <button className="formula-best-link" onClick={() => setCurveType(bestCurve.key)}>
              {bestCurve.label}
            </button> (R²={Math.round(bestCurve.r2 * 1000) / 1000})
          </div>
        )}
        <div className="formula-items">
          {renderItem('1080p', r1080p, '1080p')}
          {renderItem('720p', r720p, '720p')}
          {renderItem('Combined', rAll, 'all')}
        </div>
      </div>
    );
  };

  // Generate trend line points for the scatter chart
  const trendLineData = useMemo(() => {
    if (!data || !currentCurves || !data.scatterData.length) return null;
    const { rAll } = currentCurves;
    if (rAll.sampleCount < 2) return null;

    const xs = data.scatterData.map((d) => d.x);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const steps = 50;
    const step = (maxX - minX) / steps;
    const points = [];
    for (let i = 0; i <= steps; i++) {
      const x = minX + step * i;
      const y = rAll.predict(x);
      if (y > 0 && isFinite(y)) {
        points.push({ x: Math.round(x), y: Math.round(y * 10) / 10 });
      }
    }
    return points;
  }, [data, currentCurves]);

  if (loading) return <Loader />;

  return (
    <div>
      <DateRangeFilter
        preset={preset}
        setPreset={setPreset}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        onApply={handleApply}
        showTypeToggle
        typeFilter={typeFilter}
        setTypeFilter={handleTypeChange}
      />

      {error && <div className="error-message">{error}</div>}

      {!data && !error && (
        <div className="telemetry-empty">Select a date range and click Apply to load telemetry data.</div>
      )}

      {data && data.totalTasks === 0 && (
        <div className="telemetry-empty">No completed tasks found for the selected period.</div>
      )}

      {data && data.totalTasks > 0 && (
        <>
          {/* Summary Stats */}
          <div className="telemetry-stats-grid-5">
            <div className="stat-card">
              <p className="stat-label">Total Tasks</p>
              <p className="stat-value">{data.totalTasks}</p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Avg Processing (1080p)</p>
              <p className="stat-value" style={{ color: RESOLUTION_COLORS['1080p'] }}>
                {formatSeconds(data.avg1080p)}
              </p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Avg Processing (720p)</p>
              <p className="stat-value" style={{ color: RESOLUTION_COLORS['720p'] }}>
                {formatSeconds(data.avg720p)}
              </p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Avg Queue Wait</p>
              <p className="stat-value" style={{ color: '#a855f7' }}>
                {data.queueStats.avg != null ? formatSeconds(data.queueStats.avg) : '—'}
              </p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Avg Script Chars</p>
              <p className="stat-value">{data.avgChars.toLocaleString()}</p>
            </div>
          </div>

          {/* Formula */}
          {renderFormula()}

          {/* Chart 1: Completion Time Distribution */}
          <div className="chart-card">
            <h3 className="card-title">Completion Time Distribution</h3>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={data.distributionBuckets}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a1a2e" />
                <XAxis dataKey="bucket" stroke="#6b7280" tick={AXIS_TICK} />
                <YAxis stroke="#6b7280" tick={AXIS_TICK} />
                <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={{ color: '#9ca3af' }} />
                <Legend wrapperStyle={{ color: '#9ca3af' }} />
                <Bar dataKey="1080p" fill={RESOLUTION_COLORS['1080p']} radius={[4, 4, 0, 0]} />
                <Bar dataKey="720p" fill={RESOLUTION_COLORS['720p']} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Chart 2: Script Size vs Processing Time (Scatter + trend line) */}
          <div className="chart-card">
            <h3 className="card-title">Script Size vs Processing Time</h3>
            <p style={{ color: '#6b7280', fontSize: '0.75rem', marginBottom: '0.75rem' }}>
              processing_started_at → task_completed_at &nbsp;·&nbsp; {data.tasksWithProcessingTime} of {data.totalTasks} tasks
            </p>
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a1a2e" />
                <XAxis
                  dataKey="x"
                  type="number"
                  name="Script Chars"
                  stroke="#6b7280"
                  tick={AXIS_TICK}
                  label={{ value: 'Script Characters', position: 'insideBottom', offset: -5, fill: '#6b7280', fontSize: 12 }}
                />
                <YAxis
                  dataKey="y"
                  type="number"
                  name="Seconds"
                  stroke="#6b7280"
                  tick={AXIS_TICK}
                  label={{ value: 'Seconds', angle: -90, position: 'insideLeft', fill: '#6b7280', fontSize: 12 }}
                />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  cursor={{ strokeDasharray: '3 3', stroke: '#374151' }}
                  formatter={(value, name) => {
                    if (name === 'Seconds' || name === 'Trend') return [formatSeconds(value), name];
                    return [value, name];
                  }}
                />
                <Legend wrapperStyle={{ color: '#9ca3af' }} />
                <Scatter
                  name="1080p"
                  data={data.scatterData.filter((d) => d.resolution === '1080p')}
                  fill={RESOLUTION_COLORS['1080p']}
                />
                <Scatter
                  name="720p"
                  data={data.scatterData.filter((d) => d.resolution === '720p')}
                  fill={RESOLUTION_COLORS['720p']}
                />
                {trendLineData && (
                  <Line
                    name="Trend"
                    data={trendLineData}
                    dataKey="y"
                    stroke="#9ca3af"
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    dot={false}
                    legendType="line"
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Chart 3: Chars/Second by Resolution & Type */}
          <div className="chart-card">
            <h3 className="card-title">Average Chars/Second by Resolution & Type</h3>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={data.charsPerSecGroups}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a1a2e" />
                <XAxis dataKey="label" stroke="#6b7280" tick={AXIS_TICK} />
                <YAxis stroke="#6b7280" tick={AXIS_TICK} />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  labelStyle={{ color: '#9ca3af' }}
                  formatter={(value, name, props) => {
                    return [`${value} chars/s (${props.payload.count} tasks)`, 'Avg Chars/Sec'];
                  }}
                />
                <Bar
                  dataKey="avgCharsPerSec"
                  radius={[4, 4, 0, 0]}
                  fill={RESOLUTION_COLORS['1080p']}
                  label={false}
                >
                  {data.charsPerSecGroups.map((entry, idx) => (
                    <rect key={idx} fill={RESOLUTION_COLORS[entry.resolution] || RESOLUTION_COLORS.unknown} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Queue Time Analysis */}
          {data.queueStats.total > 0 && (
            <>
              <div className="formula-card">
                <h3 className="card-title">Queue Time Analysis</h3>
                <p style={{ color: '#6b7280', fontSize: '0.75rem', marginBottom: '0.75rem' }}>
                  Time spent waiting in queue before processing began ({data.tasksWithProcessingTime} of {data.totalTasks} tasks have processing_started_at)
                </p>
                <div className="formula-items">
                  <div className="formula-item">
                    <span className="formula-label">Avg Queue</span>
                    <span className="formula-value">{formatSeconds(data.queueStats.avg)}</span>
                  </div>
                  <div className="formula-item">
                    <span className="formula-label">Median Queue</span>
                    <span className="formula-value">{formatSeconds(data.queueStats.median)}</span>
                  </div>
                  <div className="formula-item">
                    <span className="formula-label">Min Queue</span>
                    <span className="formula-value">{formatSeconds(data.queueStats.min)}</span>
                  </div>
                  <div className="formula-item">
                    <span className="formula-label">Max Queue</span>
                    <span className="formula-value">{formatSeconds(data.queueStats.max)}</span>
                  </div>
                </div>
              </div>

              <div className="chart-card">
                <h3 className="card-title">Queue Time Distribution</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.queueDistribution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1a1a2e" />
                    <XAxis dataKey="bucket" stroke="#6b7280" tick={AXIS_TICK} />
                    <YAxis stroke="#6b7280" tick={AXIS_TICK} />
                    <Tooltip
                      contentStyle={TOOLTIP_STYLE}
                      labelStyle={{ color: '#9ca3af' }}
                      formatter={(value) => [`${value} tasks`, 'Count']}
                    />
                    <Bar dataKey="count" fill="#a855f7" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {data.queueScatter.length > 0 && (
                <div className="chart-card">
                  <h3 className="card-title">Script Size vs Queue Wait Time</h3>
                  <p style={{ color: '#6b7280', fontSize: '0.75rem', marginBottom: '0.75rem' }}>
                    created_at → processing_started_at &nbsp;·&nbsp; {data.queueScatter.length} tasks
                  </p>
                  <ResponsiveContainer width="100%" height={350}>
                    <ComposedChart>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1a1a2e" />
                      <XAxis
                        dataKey="x"
                        type="number"
                        name="Script Chars"
                        stroke="#6b7280"
                        tick={AXIS_TICK}
                        label={{ value: 'Script Characters', position: 'insideBottom', offset: -5, fill: '#6b7280', fontSize: 12 }}
                      />
                      <YAxis
                        dataKey="y"
                        type="number"
                        name="Queue Wait"
                        stroke="#6b7280"
                        tick={AXIS_TICK}
                        label={{ value: 'Queue Wait (s)', angle: -90, position: 'insideLeft', fill: '#6b7280', fontSize: 12 }}
                      />
                      <Tooltip
                        contentStyle={TOOLTIP_STYLE}
                        cursor={{ strokeDasharray: '3 3', stroke: '#374151' }}
                        formatter={(value, name) => [formatSeconds(value), name]}
                      />
                      <Legend wrapperStyle={{ color: '#9ca3af' }} />
                      <Scatter
                        name="1080p"
                        data={data.queueScatter.filter((d) => d.resolution === '1080p')}
                        fill={RESOLUTION_COLORS['1080p']}
                      />
                      <Scatter
                        name="720p"
                        data={data.queueScatter.filter((d) => d.resolution === '720p')}
                        fill={RESOLUTION_COLORS['720p']}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default EstimatedTime;
